
import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { Link } from 'react-router-dom';

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold text-[#28357B]">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-500 mb-6">{message}</p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Toast = ({ message, type, show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  const bgClass = type === 'success' ? 'bg-green-500' : 'bg-red-500';

  return (
    <div className={`fixed top-4 right-4 ${bgClass} text-white px-6 py-3 rounded shadow-lg z-[60] flex items-center space-x-2 transition-opacity duration-300`}>
      <span>{message}</span>
      <button onClick={onClose} className="text-white hover:text-gray-200">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
    </div>
  );
};

const GenericTableEditor = ({ tableName, columns, title, reorderable = false }) => {
  const [data, setData] = useState([]);
  const visibleColumns = columns.filter(col => !col.hiddenInTable);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // UI States
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    let query = supabase.from(tableName).select('*');
    
    if (reorderable) {
      query = query.order('sort_order', { ascending: true });
    } else {
      query = query.order('id', { ascending: true });
    }

    const { data, error } = await query;
    if (error) {
      setError(error.message);
    } else {
      setData(data);
    }
    setLoading(false);
  }, [tableName, reorderable]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };



  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData(item);
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirm({ show: true, id });
  };

  const handleConfirmDelete = async () => {
    const id = deleteConfirm.id;
    const { error } = await supabase.from(tableName).delete().eq('id', id);
    if (error) {
      showToast(error.message, 'error');
    } else {
      showToast('Item deleted successfully', 'success');
      fetchData();
    }
    setDeleteConfirm({ show: false, id: null });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData({ ...formData, [name]: val });
  };

  const handleFileUpload = async (e, columnKey, isList = false) => {
    try {
      setUploading(true);
      const file = e.target.files[0];
      if (!file) return;

      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('uploads')
        .upload(filePath, file, {
          cacheControl: '31536000', // 1 year cache
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage.from('uploads').getPublicUrl(filePath);

      if (isList) {
        const currentList = Array.isArray(formData[columnKey]) ? formData[columnKey] : [];
        if (currentList.length >= 5) {
          showToast('Maximum of 5 images allowed', 'error');
          return;
        }
        setFormData({ ...formData, [columnKey]: [...currentList, data.publicUrl] });
      } else {
        setFormData({ ...formData, [columnKey]: data.publicUrl });
      }
      showToast('Image uploaded successfully', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAddImageList = (columnKey, url) => {
    const currentList = Array.isArray(formData[columnKey]) ? formData[columnKey] : [];
    if (currentList.length >= 5) {
      showToast('Maximum of 5 images allowed', 'error');
      return;
    }
    setFormData({ ...formData, [columnKey]: [...currentList, url] });
  };

  const handleRemoveImageList = (columnKey, index) => {
    const currentList = Array.isArray(formData[columnKey]) ? formData[columnKey] : [];
    const newList = currentList.filter((_, i) => i !== index);
    setFormData({ ...formData, [columnKey]: newList });
  };

  const handleMoveImageList = (columnKey, index, direction) => {
    const currentList = Array.isArray(formData[columnKey]) ? formData[columnKey] : [];
    const newList = [...currentList];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newList.length) return;
    
    const temp = newList[index];
    newList[index] = newList[targetIndex];
    newList[targetIndex] = temp;
    
    setFormData({ ...formData, [columnKey]: newList });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    let submissionData = { ...formData };
    if (reorderable && editingId === 'new') {
      submissionData.sort_order = data.length;
    }

    // Auto-generate the next numeric ID for new records if it's not explicitly provided by the form
    if (editingId === 'new' && !submissionData.id) {
      const ids = data.map(item => Number(item.id)).filter(id => !isNaN(id) && isFinite(id));
      if (ids.length > 0) {
        submissionData.id = Math.max(...ids) + 1;
      }
    }

    const { error } = await supabase.from(tableName).upsert(submissionData);
    if (error) {
      showToast(error.message, 'error');
    } else {
      showToast('Item saved successfully', 'success');
      setEditingId(null);
      setFormData({});
      fetchData();
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({});
  };

  const handleAddNew = () => {
    setEditingId('new');
    setFormData({});
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = async (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    
    // Save new order to database
    const updates = data.map((item, index) => ({
      ...item,
      sort_order: index
    }));
    
    const { error } = await supabase.from(tableName).upsert(updates);
    if (error) {
      showToast('Failed to update order: ' + error.message, 'error');
    } else {
      showToast('Order updated successfully', 'success');
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newData = [...data];
    const draggedItem = newData[draggedIndex];
    newData.splice(draggedIndex, 1);
    newData.splice(index, 0, draggedItem);
    
    setDraggedIndex(index);
    setData(newData);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <ConfirmationModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this item? This action cannot be undone."
      />

      <Modal
        isOpen={!!editingId}
        onClose={handleCancel}
        title={editingId === 'new' ? 'Add New Item' : 'Edit Item'}
      >
        <form onSubmit={handleSave}>
          <div className="grid grid-cols-1 gap-4">
            {columns.map((col) => (
              <div key={col.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{col.label}</label>
                {col.type === 'textarea' ? (
                  <textarea
                    name={col.key}
                    value={formData[col.key] || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="4"
                  />
                ) : col.type === 'json' ? (
                  <textarea
                    name={col.key}
                    value={typeof formData[col.key] === 'object' ? JSON.stringify(formData[col.key], null, 2) : formData[col.key] || ''}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setFormData({ ...formData, [col.key]: parsed });
                      } catch (err) {
                        // Keep as string if invalid json
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="6"
                  />
                ) : col.type === 'select' ? (
                  <select
                    name={col.key}
                    value={formData[col.key] || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    {col.options && col.options.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : col.type === 'image' ? (
                  <div className="space-y-2">
                    {formData[col.key] && (
                      <div className="w-full h-40 bg-gray-100 rounded flex items-center justify-center overflow-hidden border">
                        <img src={formData[col.key]} alt="Preview" className="h-full object-contain" />
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        name={col.key}
                        value={formData[col.key] || ''}
                        onChange={handleChange}
                        placeholder="Enter Image URL, select from list, or upload"
                        list={`list-${col.key}`}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      {col.suggestions && (
                        <datalist id={`list-${col.key}`}>
                          {col.suggestions.map((suggestion, index) => (
                            <option key={index} value={suggestion} />
                          ))}
                        </datalist>
                      )}
                      <label className={`cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-md transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <span className="flex items-center space-x-1">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                          <span>{uploading ? '...' : 'Upload'}</span>
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, col.key)}
                          disabled={uploading}
                        />
                      </label>
                    </div>
                  </div>
                ) : col.type === 'image_list' ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-5 gap-2">
                      {(Array.isArray(formData[col.key]) ? formData[col.key] : []).map((imgUrl, idx) => (
                        <div key={idx} className="relative group aspect-square bg-gray-100 rounded border overflow-hidden">
                          <img src={imgUrl} alt={`Slide ${idx + 1}`} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-1">
                            <div className="flex justify-between font-bold">
                              <button
                                type="button"
                                onClick={() => handleMoveImageList(col.key, idx, -1)}
                                disabled={idx === 0}
                                className="text-white hover:text-blue-300 disabled:opacity-30 p-0.5"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMoveImageList(col.key, idx, 1)}
                                disabled={idx === (formData[col.key]?.length || 0) - 1}
                                className="text-white hover:text-blue-300 disabled:opacity-30 p-0.5"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveImageList(col.key, idx)}
                              className="bg-red-600 hover:bg-red-700 text-white rounded py-0.5 px-1.5 text-[10px] self-center mb-1 font-semibold"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                      {Array.from({ length: Math.max(0, 5 - (formData[col.key]?.length || 0)) }).map((_, i) => (
                        <div key={i} className="border border-dashed border-gray-300 rounded aspect-square flex items-center justify-center text-gray-400 text-[10px]">
                          Empty
                        </div>
                      ))}
                    </div>

                    {(formData[col.key]?.length || 0) < 5 && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          id={`new-img-url-${col.key}`}
                          placeholder="Enter Image URL or upload"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = e.target.value.trim();
                              if (val) {
                                handleAddImageList(col.key, val);
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.getElementById(`new-img-url-${col.key}`);
                            const val = input?.value?.trim();
                            if (val) {
                              handleAddImageList(col.key, val);
                              input.value = '';
                            }
                          }}
                          className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 font-medium"
                        >
                          Add URL
                        </button>
                        <label className={`cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-md transition-colors text-sm ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <span className="flex items-center space-x-1 font-medium">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            <span>Upload</span>
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e, col.key, true)}
                            disabled={uploading}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                ) : col.type === 'checkbox' ? (
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      name={col.key}
                      checked={!!formData[col.key]}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-500">{col.label}</span>
                  </div>
                ) : (
                  <input
                    type={col.type || 'text'}
                    name={col.key}
                    value={formData[col.key] || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className={`px-4 py-2 bg-[#28357B] text-white rounded-md hover:bg-blue-800 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Save Changes
            </button>
          </div>
        </form>
      </Modal>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#28357B]">{title}</h2>
        <Link to="/admin/dashboard" className="text-blue-600 hover:underline">Back to Dashboard</Link>
      </div>

      {error && <div className="text-red-500 mb-4 bg-red-50 p-3 rounded">{error}</div>}

      <button
        onClick={handleAddNew}
        className="mb-6 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        Add New Item
      </button>

      <div className="bg-white rounded shadow overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {reorderable && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    Order
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  No
                </th>
                {visibleColumns.map((col) => (
                  <th key={col.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {col.label}
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((item, index) => (
                <tr 
                  key={item.id} 
                  className={`hover:bg-gray-50 ${draggedIndex === index ? 'bg-blue-50' : ''}`}
                  draggable={reorderable}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                >
                  {reorderable && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 cursor-move">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
                      </svg>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {index + 1}
                  </td>
                  {visibleColumns.map((col) => (
                    <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {col.type === 'json' ? JSON.stringify(item[col.key]) : col.type === 'image' && item[col.key] ? (
                        <div className="h-10 w-10 overflow-hidden rounded bg-gray-100 border">
                          <img src={item[col.key]} alt="Thumb" className="h-full w-full object-cover" />
                        </div>
                      ) : col.type === 'checkbox' ? (
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item[col.key] ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {item[col.key] ? 'Active' : 'Inactive'}
                        </span>
                      ) : (
                        item[col.key]
                      )}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + (reorderable ? 3 : 2)} className="px-6 py-12 text-center text-gray-500">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GenericTableEditor;
